import { Injectable, inject } from '@angular/core';
import {LorenEntityFormModel} from '../../../../shared/modules/loren-entity-form-card/interfaces/loren-entity-form-model.interface.ts';
import {LorenAsset} from '../../interfaces/loren-asset.interface';
import {LorenFieldTypes} from '../../../../shared/modules/loren-entity-form-card/enums/loren-field-types.enum';
import {LorenAssetTypeService} from '../loren-asset-type-service/loren-asset-type.service';
import {KeyValuePair} from '../../../../shared/interfaces/key-value-pair.interface';
import {LorenGroupService} from '../../../loren-groups/services/loren-group-service/loren-group.service';
import {LorenDeviceService} from '../../../loren-devices/services/loren-device-service/loren-device.service';
import {map, take} from 'rxjs/operators';
import {forkJoin, Observable} from 'rxjs';
import {LorenAssetPropertiesService} from '../loren-asset-properties-service/loren-asset-properties.service';
import {LOREN_ROUTES} from '../../../../shared/constants/loren-routes.constants';
import {LorenAssetService} from '../loren-asset-service/loren-asset.service';

@Injectable({
    providedIn: 'root'
})
export class LorenAssetFormService {
    private lorenAssetTypeService = inject(LorenAssetTypeService);
    private lorenGroupService = inject(LorenGroupService);
    private propertiesService = inject(LorenAssetPropertiesService);
    private assetService = inject(LorenAssetService);
    private lorenDeviceService = inject(LorenDeviceService);


    public generateForm(entity?: LorenAsset, formName?: string): LorenEntityFormModel {
        const assetTypeOptions: KeyValuePair<string>[] = [];
        const groupOptions: KeyValuePair<number>[] = [];
        const deviceOptions: KeyValuePair<number>[] = [];

        this.prepareAssetTypes(assetTypeOptions);
        this.prepareGroups(groupOptions);
        this.prepareDevices(deviceOptions, entity);

        return {
            formName,
            deleteActionPath: entity ? LOREN_ROUTES.ASSETS_.delete(entity.id) : null,
            cancelActionPath: LOREN_ROUTES.ASSETS_.all(),
            formFields: [
                {
                    name: 'name',
                    label: 'asset.name_property',
                    value: entity ? entity.name : null,
                    formControlType: LorenFieldTypes.INPUT,
                    validator: {
                        required: true
                    },
                    options: []
                },
                {
                    name: 'type',
                    label: 'asset.type_property',
                    value: entity ? String(entity.typeId) : null,
                    disabled: entity && entity.id ? true : false,
                    formControlType: LorenFieldTypes.SELECT,
                    validator: {
                        required: true
                    },
                    options: assetTypeOptions
                },
                {
                    name: 'belongsToGroups',
                    label: 'asset.group_property',
                    multiple: true,
                    values: entity ? entity.belongsToGroups : [],
                    formControlType: LorenFieldTypes.SELECT,
                    options: groupOptions
                },
                // {
                //     name: 'devices',
                //     label: 'asset.device_property',
                //     multiple: true,
                //     values: entity ? entity.devices : [],
                //     formControlType: LorenFieldTypes.SELECT,
                //     options: deviceOptions
                // },
                {
                    name: 'description',
                    label: 'asset.description_property',
                    value: entity ? entity.description ?? `` : null,
                    formControlType: LorenFieldTypes.TEXTAREA,
                    options: []
                },
            ]
        };
    }

    public generateAssetTypePropertiesForm(typeId: number, formName?: string): Observable<LorenEntityFormModel> {
        const result: LorenEntityFormModel = {
            formFields: [],
            cancelActionPath: '',
            deleteActionPath: '',
            formName
        };

        return forkJoin([
            this.lorenAssetTypeService.get(typeId),
            this.propertiesService.query()
        ]).pipe(take(1), map(res => {
            const assetType = res[0];
            const properties = res[1].items;
            const assetTypeProperties = assetType.properties;

            properties.filter(x => assetTypeProperties.indexOf(x.id) !== -1).forEach(property => {
                result.formFields.push({
                    formControlType: property.formControlType,
                    name: String(property.name),
                    value: property.value ?? null,
                    label: property.label,
                    options: [],
                    orderInForm: property.orderInForm,
                    placeholder: ''
                });
            });

            return result;
        }));
    }

    private prepareAssetTypes(assetTypeOptions: KeyValuePair<string>[]) {
        this.lorenAssetTypeService.query().pipe(take(1)).subscribe(res => {
            res.items.forEach(assetType => {
                assetTypeOptions.push({
                    key: assetType.label,
                    value: String(assetType.id)
                });
            });
        });
    }

    private prepareDevices(deviceOptions: KeyValuePair<number>[], currentAsset: LorenAsset) {
        forkJoin([
            this.assetService.query().pipe(take(1)),
            this.lorenDeviceService.query().pipe(take(1))
        ]).pipe(take(1)).subscribe(res => {
            const usedDevices: number[] = [];
            const assets = res[0].items;

            assets.forEach(x => {
                if (currentAsset && x.id === currentAsset.id) {
                    return;
                }

                x.devices.forEach(d => {
                    usedDevices.push(d);
                });
            });

            res[1].items.forEach(entity => {
                if (usedDevices.indexOf(entity.id) !== -1) {
                    return;
                }

                deviceOptions.push({
                    key: entity.name,
                    value: entity.id
                });
            });
        });
    }

    private prepareGroups(groupOptions: KeyValuePair<number>[]) {
        this.lorenGroupService.query().pipe(take(1)).subscribe(res => {
            res.items.forEach(entity => {
                groupOptions.push({
                    key: entity.name,
                    value: entity.id
                });
            });
        });
    }
}
